import React, { useState, useEffect, useContext } from 'react';
import { Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';

import { client as publicClientApplication } from '../../../utilities/AuthUtils';
import PrivilegesContext from '../../../contexts/PrivilegesContext';
import { useCreateQIR, useGetQIR, useUpdateQIR } from '../../../hooks/useQIRs';
import DeleteQIRButton from './DeleteQIRButton';
import QIRComponent from './QIRComponent';

function UpsertQIR({action}) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { qirId } = useParams();
    const { admin } = useContext(PrivilegesContext);
    const { createQIR, loading: loadingCreateQIR } = useCreateQIR();
    const { qir: qirDetail, getQIR, loading: loadingGetQIR } = useGetQIR(qirId);
    const { updateQIR, loading: loadingUpdateQIR } = useUpdateQIR();
    const [qir, setQir] = useState({});

    useEffect(() => {
        switch (action) {
            case "create":
                const account = publicClientApplication.getAllAccounts()[0];
                setQir(q => ({
                    ...q,
                    originator: account.name
                }));
                break;
            case "update":
                setQir(qirDetail);
                break;
            default:
                throw new Error(`Invalid action for UpsertQIR componet, expected create or update but got ${action}`);
        }
    }, [action, qirDetail]);

    const saveChanges = async (comment, commentReasonCodeId) => {
        qir.comment = comment;
        qir.commentReasonCodeId = commentReasonCodeId;
        switch (action) {
            case "create":
                await createQIRAndNavigateBackToList(qir);
                break;
            case "update":
                await updateQIRAndRefresh(qir);
                break;
            default:
                throw new Error(`Invalid action for UpsertQIR componet, expected create or update but got ${action}`);
        }
    }

    const createQIRAndNavigateBackToList = async () => {
        let success = await createQIR(qir);
        if (success) {
            navigate("/qirs");
        }
    }

    const updateQIRAndRefresh = async (qir) => {
        let success = await updateQIR(qir)
        if (success) {
            getQIR();
        }
    }

    if (action === "update" && (loadingGetQIR || loadingUpdateQIR)) {
        return (
            <div className="d-flex justify-content-center">
                <Spinner animation="border" />
            </div>
        );
    }

    if (action === "create" && loadingCreateQIR) {
        return (
            <div className="d-flex justify-content-center">
                <Spinner animation="border" />
            </div>
        );
    }

    if (qir === undefined) {
        return (
            <h4 className="d-flex justify-content-center">{t("QIRDetail.QIRFailedToLoad")}</h4>
        )
    }

    return (
        <>
            {action === "create" &&
                <Helmet>
                    <title>{t("CreateQIR.TabTitle")} </title>
                </Helmet>
            }
            {action === "update" && admin && !qir?.isDeleted &&
                <div className="d-flex justify-content-end me-3 mb-2">
                    <DeleteQIRButton qirId={qirId} />
                </div>
            }
            {qir?.isDeleted &&
                <p className="text-center">{t("QIRDetail.DeletedQIR")}</p>
            }
            <QIRComponent
                qir={qir}
                setQir={setQir}
                action={action}
                submitButtonText={action === "create" ? t("CreateQIR.CreateButton") : t("UpdateQIR.UpdateButton")}
                onSave={saveChanges}
                refresh={action === "create" ? null : getQIR}
            />
        </>
    );
}

export default UpsertQIR;